import React, { useCallback, useEffect, useState } from "react";
import { default as ReCAPTCHA } from "react-google-recaptcha";
import styled from "styled-components";
import { useImmer } from "use-immer";

import { gql } from "@examspark/api";
import { doNothing } from "@examspark/common";
import {
  Button,
  Callout,
  Form,
  Link,
  RouterLink,
  TextInput,
  Toolbar,
  VSpace,
} from "@examspark/common-ui";
import {
  IconAcceptLine,
  IconRemoveLine,
} from "@examspark/common-ui/dist/components/icons";
import { AppLicense, RegistrationPass, WorkspaceUser } from "@examspark/types";

import {
  AmplifyCognitoUser,
  confirmSignUp,
  createCognitoUser,
  currentAuthenticatedUser,
  resendCode,
  signIn,
} from "../../auth";
import { useLogout } from "../../hooks/useLogout";
import { useAppContext } from "../../providers/AppContext";

const Text = styled.div`
  color: #333;
  font-size: 1.3rem;
  line-height: 1.5em;
`;
const RegistrationTitle = styled.h1`
  margin: 0 0 2rem;
  font-weight: normal;
  color: #333;
  text-align: center;
`;

interface RegistrationFormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  workspaceId: string;
  registrationCode: string;
}

const emptyFormData: RegistrationFormData = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  workspaceId: "",
  registrationCode: "",
};

interface Props {
  onRegister?: () => void;
  onBackToLogin?: () => void;
}

const Register = (props: Props) => {
  const [state, dispatch] = useAppContext();
  const logout = useLogout();
  const isDevelopment = process.env.NODE_ENV === "development";
  const [isSaving, setIsSaving] = useState(false);
  const [isResendingCode, setIsResendingCode] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [shouldSkipConfirmation, setShouldSkipConfirmation] = useState(false);

  // The flow is, generally, as follows:
  //   view:
  //     form -> register-new -> confirmation-code -> complete
  //   actions:
  //     api.checkRegistrationStatus -> cognito.signUp ->  cognito.confirmCode -> api.registerCogitoUser -> api.registerAppUser
  // or, if they already have a cognito identity
  //   view:
  //     form -> register-existing -> complete
  //   actions:
  //     api.checkRegistrationStatus -> api.registerAppUser
  const [view, setView] = useState<
    | "form"
    | "register-new"
    | "confirmation-code"
    | "register-existing"
    | "complete"
  >("form");
  const [isCaptchaComplete, setIsCaptchaComplete] = useState(
    isDevelopment ? true : false,
  );
  const [registrationPass, setRegistrationPass] = useState<RegistrationPass>();

  const [form, updateForm] = useImmer<RegistrationFormData>(
    isDevelopment
      ? {
          email: process.env.REACT_APP_REGISTRATION_EMAIL ?? "",
          password: process.env.REACT_APP_REGISTRATION_PASSWORD ?? "",
          firstName: process.env.REACT_APP_REGISTRATION_FIRST_NAME ?? "",
          lastName: process.env.REACT_APP_REGISTRATION_LAST_NAME ?? "",
          workspaceId: process.env.REACT_APP_REGISTRATION_WORKSPACE_ID ?? "",
          registrationCode:
            process.env.REACT_APP_REGISTRATION_REGISTRATION_CODE ?? "",
        }
      : emptyFormData,
  );
  const [confirmationCode, setConfirmationCode] = useState("");

  const checkLoggedInUser = useCallback(async () => {
    const user = await currentAuthenticatedUser();

    updateForm((f) => {
      f.email = user?.username ?? "";
    });
    setIsLoggedIn(user != null);
  }, [updateForm]);

  const checkRegistrationStatus = async () => {
    try {
      setIsSaving(true);

      const normalizedForm = {
        ...form,
        email: form.email.trim().toLowerCase(),
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        workspaceId: form.workspaceId.trim(),
        registrationCode: form.registrationCode.trim(),
      };

      validateRegistrationForm(normalizedForm, view);

      const registrationStatus =
        await gql.operations.registration.determineRegistrationStatus({
          workspaceId: normalizedForm.workspaceId,
          email: normalizedForm.email,
          code: normalizedForm.registrationCode,
        });

      if (!registrationStatus.data?.determineRegistrationStatus) {
        throw new Error("Invalid registration status");
      }

      // temporary logging
      console.info(registrationStatus.data.determineRegistrationStatus);
      setRegistrationPass(registrationStatus.data.determineRegistrationStatus);

      if (registrationStatus.data.determineRegistrationStatus.isRecognized) {
        if (isLoggedIn) {
          await registerExistingUser(
            registrationStatus.data.determineRegistrationStatus,
          );
        } else if (
          registrationStatus.data?.determineRegistrationStatus.userStatus ===
          "UNCONFIRMED"
        ) {
          handleResendCode();
          setConfirmationCode("");
          setView("confirmation-code");
        } else {
          // handle cases where we have a confirmed cognito user but no user record
          if (registrationStatus.data?.determineRegistrationStatus.isCreated) {
            setView("register-existing");
          } else {
            setShouldSkipConfirmation(true);
            setView("register-new");
          }
        }
      } else {
        setView("register-new");
      }
    } catch (error) {
      /** @todo this is a candidate for `assertAppSyncError` in `common` */
      if (
        typeof error === "object" &&
        error !== null &&
        "errors" in error &&
        error.errors &&
        Array.isArray(error.errors) &&
        error.errors.length === 1
      ) {
        const firstError = error.errors[0];
        switch (firstError.errorType) {
          case "invalid-code":
            alert(
              "Invalid registration code. Please double-check and try again.",
            );
            return;
          case "invalid-email":
            alert(
              "The email domain you entered does not match those approved for this workspace. Please contact an administrator for assistance.",
            );
            return;
          case "workspace-registration-disabled":
            alert(firstError.message);
            return;
          default:
            alert(firstError.message);
            return;
        }
      }

      console.error(error);
      alert(error);
    } finally {
      setIsSaving(false);
    }
  };

  const registerNewUser = async () => {
    try {
      setIsSaving(true);

      if (!registrationPass?.jwt) {
        throw new Error("Missing registration pass jwt");
      }

      // Normalize form data
      const normalizedEmail = form.email.trim().toLowerCase();
      const normalizedFirstName = form.firstName.trim();
      const normalizedLastName = form.lastName.trim();

      if (shouldSkipConfirmation) {
        const signInResult = await signIn(normalizedEmail, form.password);

        await gql.operations.registration.registerCognitoUser({
          firstName: normalizedFirstName,
          lastName: normalizedLastName,
          jwt: registrationPass?.jwt,
        });

        await gql.operations.registration.registerAppUser({
          jwt: registrationPass?.jwt,
        });

        await finishRegistration(signInResult.attributes.sub);
      } else {
        await createCognitoUser(normalizedEmail, form.password);
        setConfirmationCode("");
        setView("confirmation-code");
      }
    } catch (error) {
      console.error(error);
      alert(error);
    } finally {
      setIsSaving(false);
    }
  };

  const confirmRegisterNewUser = async () => {
    try {
      setIsSaving(true);

      if (!registrationPass?.jwt) {
        throw new Error("Missing registration pass jwt");
      }

      if (form.password == null || form.password === "") {
        throw new Error("Please enter a password to login.");
      }

      // Normalize form data
      const normalizedEmail = form.email.trim().toLowerCase();
      const normalizedFirstName = form.firstName.trim();
      const normalizedLastName = form.lastName.trim();

      await confirmSignUp(normalizedEmail, confirmationCode.trim());

      const signInResult = await signIn(normalizedEmail, form.password);

      await gql.operations.registration.registerCognitoUser({
        firstName: normalizedFirstName,
        lastName: normalizedLastName,
        jwt: registrationPass?.jwt,
      });

      await gql.operations.registration.registerAppUser({
        jwt: registrationPass?.jwt,
      });

      await finishRegistration(signInResult.attributes.sub);
    } catch (error) {
      console.error(error, {
        email: form.email.trim().toLowerCase(),
        code: form.registrationCode,
        workspaceId: form.workspaceId.trim(),
        view: view,
      });
      alert(error);
    } finally {
      setIsSaving(false);
    }
  };

  const registerExistingUser = async (pass?: RegistrationPass) => {
    let user: AmplifyCognitoUser | undefined;

    const jwt = pass?.jwt ?? registrationPass?.jwt;

    try {
      setIsSaving(true);

      if (!jwt) {
        throw new Error("Missing registration pass jwt");
      }

      user = await currentAuthenticatedUser();

      if (user == null) {
        if (form.password == null || form.password === "") {
          throw new Error("Please enter a password to login.");
        }
        user = await signIn(form.email.trim().toLowerCase(), form.password);
      }

      await gql.operations.registration.registerAppUser({ jwt });

      await finishRegistration(user.attributes.sub);

      setView("complete");
    } catch (error) {
      console.error(error, {
        email: form.email.trim().toLowerCase(),
        code: form.registrationCode.trim(),
        workspaceId: form.workspaceId.trim(),
        view: view,
      });
      alert(error);
    } finally {
      setIsSaving(false);
    }
  };

  const finishRegistration = async (id: string) => {
    const user = await gql.operations.user.getUser(id);
    if (!user) {
      throw new Error("Unable to create user.");
    }

    let appLicense: AppLicense | undefined;

    if (state.app?.id != null) {
      appLicense = await gql.operations.app.getAppLicense(
        state.app.id,
        form.workspaceId.trim(),
      );

      if (!appLicense) throw new Error("Unable to get app license.");
    }

    const workspaceUser = await gql.operations.workspace.getWorkspaceUser(
      form.workspaceId.trim(),
      user.id,
    );

    if (!workspaceUser) {
      throw new Error(`Unable to add user to workspace`);
    }

    dispatch({
      type: "SET_USER",
      data: user,
    });
    dispatch({
      type: "SET_WORKSPACE",
      data: {
        workspaceUser: workspaceUser as WorkspaceUser,
        workspace: workspaceUser.workspace,
        appLicense,
      },
    });

    props.onRegister?.();

    setView("complete");
  };

  const handleResendCode = async () => {
    try {
      if (!form.email) throw new Error("Missing email address");
      setIsResendingCode(true);
      await resendCode(form.email);
      setIsResendingCode(false);
      window.alert(
        "Your code has been resent. Check your email (and possibly the spam folder) for this code.",
      );
    } catch (error) {
      setIsResendingCode(false);
    }
  };

  // this is bad. A capchta that is validated user-side makes it pointless.
  const handleCaptchaChange = (code: string | null) => {
    setIsCaptchaComplete(!!code);
  };

  useEffect(() => {
    checkLoggedInUser();
  }, [checkLoggedInUser]);

  if (view === "confirmation-code") {
    return (
      <div>
        <RegistrationTitle>Enter Confirmation Code</RegistrationTitle>
        <Text style={{ margin: "2rem 0" }}>
          We just sent a confirmation code to your email address. Enter that
          code in the field below. Didn't receive it? Check your spam folder.
        </Text>
        <Form>
          <Form.FieldList>
            <Form.Field label="Password">
              <TextInput
                name="password"
                type="password"
                value={form.password}
                onChange={(e) =>
                  updateForm((draft) => {
                    draft.password = e.target.value;
                  })
                }
              />
            </Form.Field>
            <Form.Field label="Confirmation Code">
              <TextInput
                maxLength={6}
                name="code"
                value={confirmationCode}
                onChange={(e) => setConfirmationCode(e.target.value)}
              />
            </Form.Field>
            <Toolbar>
              <Button
                title="Confirm Registration"
                variant="contained"
                stretch
                color="primary"
                onClick={confirmRegisterNewUser}
                isLoading={isSaving}
                disabled={
                  confirmationCode.length !== 6 || isSaving || !form.password
                }
              >
                Confirm Registration
              </Button>
            </Toolbar>
            <Toolbar>
              <Button
                disabled={!isCaptchaComplete}
                isLoading={isResendingCode}
                title="Resend Code"
                variant="contained"
                stretch
                onClick={handleResendCode}
              >
                Resend Code
              </Button>
            </Toolbar>
            <RouterLink to="." onClick={() => setView("form")}>
              Return to Registration
            </RouterLink>
          </Form.FieldList>
        </Form>
      </div>
    );
  }

  let action: () => Promise<void> | void;
  switch (view) {
    case "form":
      action = checkRegistrationStatus;
      break;
    case "register-new":
      action = registerNewUser;
      break;
    case "register-existing":
      action = registerExistingUser;
      break;
    default:
      action = doNothing;
  }

  return (
    <div>
      <RegistrationTitle>User Registration</RegistrationTitle>
      <Form>
        <VSpace>
          {view === "register-existing" && (
            <Callout icon={<IconAcceptLine width="16" />}>
              <VSpace>
                <div>
                  You're almost set! This application uses Campfire Learning for
                  login, and it looks like you already have a Campfire
                  account.{" "}
                </div>
                <div>
                  If you remember your Campfire email/password associated with
                  this account, fill it in below.
                </div>
                <div>
                  If you don't remember it, click "Back to Login", then "Reset
                  Password". Once reset, you can try registering again.
                </div>
              </VSpace>
            </Callout>
          )}
          {view === "register-new" && (
            <Text>
              We just need a little more information to get you set up. Please
              fill out the form below.
            </Text>
          )}
          <Form.FieldList>
            <Form.Field label="Email Address">
              <div style={{ display: "flex" }}>
                <TextInput
                  style={{ width: "100%" }}
                  name="email"
                  value={form.email}
                  disabled={
                    view === "register-existing" ||
                    view === "register-new" ||
                    isLoggedIn
                  }
                  onChange={(e) =>
                    updateForm((draft) => {
                      draft.email = e.target.value;
                    })
                  }
                />
                {isLoggedIn && (
                  <Button
                    style={{ marginLeft: "0.5rem" }}
                    onClick={() => {
                      logout().then(checkLoggedInUser);
                    }}
                    title="logout"
                    variant="plain"
                    icon={<IconRemoveLine />}
                  />
                )}
              </div>
            </Form.Field>
            <Form.Field label="Workspace ID">
              <TextInput
                type="text"
                name="workspace_id"
                disabled={
                  view === "register-existing" || view === "register-new"
                }
                value={form.workspaceId}
                onChange={(e) =>
                  updateForm((draft) => {
                    draft.workspaceId = e.target.value;
                  })
                }
              />
            </Form.Field>

            <Form.Field label="Registration Code">
              <TextInput
                type="text"
                name="code"
                value={form.registrationCode}
                disabled={
                  view === "register-existing" || view === "register-new"
                }
                onChange={(e) =>
                  updateForm((draft) => {
                    draft.registrationCode = e.target.value;
                  })
                }
              />
            </Form.Field>

            {view === "register-new" && (
              <>
                <Form.Field label="First Name">
                  <TextInput
                    name="first_name"
                    value={form.firstName}
                    onChange={(e) =>
                      updateForm((draft) => {
                        draft.firstName = e.target.value;
                      })
                    }
                  />
                </Form.Field>
                <Form.Field label="Last Name">
                  <TextInput
                    name="username"
                    value={form.lastName}
                    onChange={(e) =>
                      updateForm((draft) => {
                        draft.lastName = e.target.value;
                      })
                    }
                  />
                </Form.Field>
              </>
            )}

            {(view === "register-new" || view === "register-existing") && (
              <Form.Field label="Password">
                <TextInput
                  name="password"
                  type="password"
                  value={form.password}
                  onChange={(e) =>
                    updateForm((draft) => {
                      draft.password = e.target.value;
                    })
                  }
                />
              </Form.Field>
            )}

            {!isDevelopment && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  margin: "2rem 0",
                }}
              >
                <ReCAPTCHA
                  sitekey="6Lcvq7AhAAAAAHs5UctDEEhZR1xKsUu5GfBRadr2"
                  onChange={handleCaptchaChange}
                />
              </div>
            )}

            <Toolbar>
              <Button
                disabled={isSaving || (!isDevelopment && !isCaptchaComplete)}
                title="Register"
                variant="contained"
                stretch
                color="primary"
                isLoading={isSaving}
                onClick={() => action()}
              >
                Register
              </Button>
            </Toolbar>
            <Link
              onClick={async () => {
                if (!state.sso) {
                  await logout();
                }
                props.onBackToLogin?.();
              }}
            >
              Back to Login
            </Link>
          </Form.FieldList>
        </VSpace>
      </Form>
    </div>
  );
};

const validateRegistrationForm = (form: RegistrationFormData, view: string) => {
  if (view === "register-new") {
    if (!form.firstName?.trim().length) {
      throw new Error("Missing first name.");
    }
    if (!form.lastName?.trim().length) {
      throw new Error("Missing last name.");
    }
  }

  if (view === "register-existing" || view === "register-new") {
    if (!form.password?.length) {
      throw new Error("Missing password");
    }
  }

  if (!form.email?.trim().length) {
    throw new Error("Missing email address");
  }
  if (!form.workspaceId?.trim().length) {
    throw new Error("Missing workspace ID");
  }
  if (!form.registrationCode?.trim().length) {
    throw new Error("Missing registration code");
  }
};

export default Register;
